import { DescriptionPage_MarketingSource as MarketingSource } from '__generated__/graphql-types';

import type { DegreesCollectionsContextType } from 'bundles/unified-description-page-common/contexts/DegreesCollectionsContext';
import type { CollectionEntityFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/CollectionEntityFragment';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import type { EnterpriseCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseCoursePageQuery';
import type { EnterpriseSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseSpecializationPageQuery';
import type { PageConfig } from 'bundles/unified-description-page-common/types/moduleConfig';
import { ModuleNames } from 'bundles/unified-description-page-common/types/moduleConfig';

import _t from 'i18n!nls/xdp';

type NavigationLink = { name: string; label: string };
type CollectionEntityType = CollectionEntityFragment[] | undefined;

export const COURSE_NAV_ITEMS = {
  ABOUT: 'about',
  OUTCOMES: 'outcomes',
  MODULES: 'modules',
  RECOMMENDATIONS: 'recommendations',
  TESTIMONIALS: 'testimonials',
  REVIEWS: 'reviews',
};

export function getCoursePageNavigationLinks({
  pageData,
  collections,
  marketingSource,
  pageConfig,
}: {
  pageData: ConsumerCoursePageFieldsFragment | EnterpriseCoursePageFieldsFragment;
  collections?: DegreesCollectionsContextType;
  marketingSource?: MarketingSource;
  pageConfig?: PageConfig;
}) {
  // TODO: update conditions for showOutcomes and showTestimonials when their modules are added.
  const showTestimonials = true;
  const relatedRecommendations = pageData?.relatedRecommendations as CollectionEntityType;

  const showReviews = pageData?.topReviews && pageData.topReviews.length > 0;
  const showOutcomes = pageData.specializations && pageData.specializations.length > 0;
  const hasStandardRecommendations =
    relatedRecommendations?.length || (collections?.degrees && !collections?.degreeRecommendationsLoading);
  const showRecommendations = hasStandardRecommendations;
  const showModules = pageData?.material?.weeks && pageData.material.weeks.length > 0;

  const pageConfiguratation = pageConfig;

  const navLinks: Array<NavigationLink> = [];

  // TODO: remove the if block when removing the existing {pageType}PageRootContent components.
  if (!pageConfiguratation) {
    navLinks.push({ name: COURSE_NAV_ITEMS.ABOUT, label: _t('About') });

    if (showOutcomes) {
      navLinks.push({ name: COURSE_NAV_ITEMS.OUTCOMES, label: _t('Outcomes') });
    }

    if (showModules) {
      navLinks.push({ name: COURSE_NAV_ITEMS.MODULES, label: _t('Modules') });
    }

    if (
      showRecommendations &&
      (!marketingSource || (marketingSource && marketingSource !== MarketingSource.PaidMedia))
    ) {
      navLinks.push({ name: COURSE_NAV_ITEMS.RECOMMENDATIONS, label: _t('Recommendations') });
    }

    if (showTestimonials) {
      navLinks.push({ name: COURSE_NAV_ITEMS.TESTIMONIALS, label: _t('Testimonials') });
    }

    if (showReviews) {
      navLinks.push({ name: COURSE_NAV_ITEMS.REVIEWS, label: _t('Reviews') });
    }

    return navLinks;
  }

  pageConfiguratation?.modules.forEach((moduleConfig) => {
    switch (moduleConfig.name) {
      case ModuleNames.ProductOverview:
        navLinks.push({ name: COURSE_NAV_ITEMS.ABOUT, label: _t('About') });
        break;
      case ModuleNames.CareerOutcomes:
        if (showOutcomes) {
          navLinks.push({ name: COURSE_NAV_ITEMS.OUTCOMES, label: _t('Outcomes') });
        }
        break;
      case ModuleNames.Syllabus:
        if (showModules) {
          navLinks.push({ name: COURSE_NAV_ITEMS.MODULES, label: _t('Modules') });
        }
        break;
      case ModuleNames.Testimonials:
        navLinks.push({ name: COURSE_NAV_ITEMS.TESTIMONIALS, label: _t('Testimonials') });
        break;
      case ModuleNames.Recommendations:
        if (showRecommendations) {
          navLinks.push({
            name: COURSE_NAV_ITEMS.RECOMMENDATIONS,
            label: _t('Recommendations'),
          });
        }
        break;
      case ModuleNames.Reviews:
        if (showReviews) {
          navLinks.push({
            name: COURSE_NAV_ITEMS.REVIEWS,
            label: _t('Reviews'),
          });
        }
        break;
      default:
        // no op
        break;
    }
  });

  return navLinks;
}

export const SPECIALIZATION_NAV_ITEMS = {
  ABOUT: 'about',
  OUTCOMES: 'outcomes',
  COURSES: 'courses',
  TESTIMONIALS: 'testimonials',
  RECOMMENDATIONS: 'recommendations',
};

type OverrideConfig = {
  showRecommendations?: boolean;
};

export function getSpecializationPageNavigationLinks({
  pageData,
  pageConfig,
  overrides = {},
}: {
  pageData: ConsumerSpecializationPageFieldsFragment | EnterpriseSpecializationPageFieldsFragment;
  pageConfig?: PageConfig;
  overrides?: OverrideConfig;
}) {
  // TODO: update conditions for showTestimonials when its module is added.
  const showTestimonials = true;

  const showRecommendations = overrides.showRecommendations ?? false;

  const showCourses = pageData?.courses && pageData.courses.length > 0;

  const pageConfiguratation = pageConfig;

  const navLinks: Array<NavigationLink> = [];

  // TODO: remove the if block when removing the existing {pageType}PageRootContent components.
  if (!pageConfiguratation) {
    navLinks.push({ name: SPECIALIZATION_NAV_ITEMS.ABOUT, label: _t('About') });
    navLinks.push({ name: SPECIALIZATION_NAV_ITEMS.OUTCOMES, label: _t('Outcomes') });

    if (showCourses) {
      navLinks.push({ name: SPECIALIZATION_NAV_ITEMS.COURSES, label: _t('Courses') });
    }

    if (showTestimonials) {
      navLinks.push({ name: SPECIALIZATION_NAV_ITEMS.TESTIMONIALS, label: _t('Testimonials') });
    }

    if (showRecommendations) {
      navLinks.push({ name: SPECIALIZATION_NAV_ITEMS.RECOMMENDATIONS, label: _t('Recommendations') });
    }

    return navLinks;
  }

  pageConfiguratation?.modules.forEach((moduleConfig) => {
    switch (moduleConfig.name) {
      case ModuleNames.ProductOverview:
        navLinks.push({ name: SPECIALIZATION_NAV_ITEMS.ABOUT, label: _t('About') });
        break;
      case ModuleNames.CareerOutcomes:
        navLinks.push({
          name: SPECIALIZATION_NAV_ITEMS.OUTCOMES,
          label: _t('Outcomes'),
        });
        break;
      case ModuleNames.Syllabus:
        if (showCourses) {
          navLinks.push({
            name: SPECIALIZATION_NAV_ITEMS.COURSES,
            label: _t('Courses'),
          });
        }
        break;
      case ModuleNames.Testimonials:
        navLinks.push({
          name: SPECIALIZATION_NAV_ITEMS.TESTIMONIALS,
          label: _t('Testimonials'),
        });
        break;
      case ModuleNames.Recommendations:
        if (showRecommendations) {
          navLinks.push({
            name: SPECIALIZATION_NAV_ITEMS.RECOMMENDATIONS,
            label: _t('Recommendations'),
          });
        }
        break;
      default:
        // no op
        break;
    }
  });

  return navLinks;
}

export const PROJECT_NAV_ITEMS = {
  ABOUT: 'about',
  OUTCOMES: 'outcomes',
  DETAILS: 'details',
  TESTIMONIALS: 'testimonials',
  REVIEWS: 'reviews',
  RECOMMENDATIONS: 'recommendations',
};

export function getProjectPageNavigationLinks({
  showOutcomes = true,
  pageData,
  pageConfig,
}: {
  showOutcomes?: boolean;
  pageData?: ConsumerCoursePageFieldsFragment | EnterpriseCoursePageFieldsFragment;
  pageConfig?: PageConfig;
}) {
  const { topReviews, relatedRecommendations } = pageData || {};
  const relatedRecs = relatedRecommendations as CollectionEntityType;

  // TODO: update conditions for the following variables when their modules are added.
  const showTestimonials = true;

  const showReviews = topReviews && topReviews.length > 0;
  const hasStandardRecommendations = relatedRecs && relatedRecs.length > 0;
  const showRecommendations = hasStandardRecommendations;

  const pageConfiguratation = pageConfig;

  const navLinks: Array<NavigationLink> = [];

  // TODO: remove the if block when removing the existing {pageType}PageRootContent components.
  if (!pageConfiguratation) {
    navLinks.push({ name: PROJECT_NAV_ITEMS.ABOUT, label: _t('About') });

    if (showOutcomes) {
      navLinks.push({ name: PROJECT_NAV_ITEMS.OUTCOMES, label: _t('Outcomes') });
    }

    navLinks.push({ name: PROJECT_NAV_ITEMS.DETAILS, label: _t('Project details') });

    if (showTestimonials) {
      navLinks.push({ name: PROJECT_NAV_ITEMS.TESTIMONIALS, label: _t('Testimonials') });
    }

    if (showReviews) {
      navLinks.push({ name: PROJECT_NAV_ITEMS.REVIEWS, label: _t('Reviews') });
    }

    if (showRecommendations) {
      navLinks.push({ name: PROJECT_NAV_ITEMS.RECOMMENDATIONS, label: _t('Recommendations') });
    }

    return navLinks;
  }

  pageConfiguratation?.modules.forEach((moduleConfig) => {
    switch (moduleConfig.name) {
      case ModuleNames.ProductOverview:
        navLinks.push({ name: PROJECT_NAV_ITEMS.ABOUT, label: _t('About') });
        break;
      case ModuleNames.CareerOutcomes:
        navLinks.push({ name: PROJECT_NAV_ITEMS.OUTCOMES, label: _t('Outcomes') });
        break;
      case ModuleNames.Syllabus:
        navLinks.push({ name: PROJECT_NAV_ITEMS.DETAILS, label: _t('Project details') });
        break;
      case ModuleNames.Testimonials:
        if (showTestimonials) {
          navLinks.push({ name: PROJECT_NAV_ITEMS.TESTIMONIALS, label: _t('Testimonials') });
        }
        break;
      case ModuleNames.Recommendations:
        if (showRecommendations) {
          navLinks.push({ name: PROJECT_NAV_ITEMS.RECOMMENDATIONS, label: _t('Recommendations') });
        }
        break;
      case ModuleNames.Reviews:
        if (showReviews) {
          navLinks.push({ name: PROJECT_NAV_ITEMS.REVIEWS, label: _t('Reviews') });
        }
        break;
      default:
        // no op
        break;
    }
  });

  return navLinks;
}
